import React from "react";

function display(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

export default function DebateReportPanel({ report }) {
  if (!report) return null;
  return (
    <div className="space-y-10">
      <section className="border border-white/10 p-6 sm:p-8">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">Reasoning Report Card</div>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">{display(report.reasoning_level)}</h1>
            <p className="text-sm text-forge-100/50 mt-2">{display(report.reading_title)}</p>
            {report.fallback_used && (
              <div className="mt-3 inline-flex border border-gold-500/35 bg-gold-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold-400">
                Fallback report card generated.
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-1">Overall</div>
            <div className="font-serif text-5xl text-ember-400">{display(report.overall_score)}</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/40 mt-1">out of 100</div>
          </div>
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-forge-100/40">
          Completed {report.completed_at ? report.completed_at.slice(0, 10) : "-"}
        </div>
      </section>

      {report.synthesis && (
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Final Evaluation</div>
          <p className="font-reading text-lg leading-relaxed border-l-2 border-ember-500 pl-5 text-forge-100/90">{report.synthesis}</p>
        </section>
      )}

      <section>
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-5">Category Scores</div>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
          {(report.category_scores || []).map((category) => (
            <div key={category.key} className="flex items-center justify-between gap-4">
              <span className="text-sm text-forge-100/85">{category.label}</span>
              <div className="flex items-center gap-3 flex-1 max-w-[180px]">
                <div className="flex-1 h-1.5 bg-white/8 overflow-hidden">
                  <div className="h-full bg-ember-500" style={{ width: `${(category.score / 10) * 100}%` }} />
                </div>
                <span className="font-mono text-xs text-forge-100/60 w-10 text-right">{category.score}/10</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-px bg-white/5">
        <ListBlock tone="ember" title="Strongest Categories" items={(report.strongest_categories || []).map((c) => `${c.label}: ${c.score}/10`)} />
        <ListBlock tone="gold" title="Growth Edges" items={(report.growth_edges || []).map((c) => `${c.label}: ${c.score}/10`)} />
      </section>

      <section className="grid sm:grid-cols-2 gap-px bg-white/5">
        <ListBlock tone="ember" title="Strengths" items={report.strengths || []} />
        <ListBlock tone="gold" title="Improvement Areas" items={report.improvement_areas || report.weaknesses || []} />
      </section>

      {report.coaching_feedback && (
        <section className="border-l-2 border-gold-500 pl-5 py-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-2">Coaching Feedback</div>
          <p className="font-reading text-base sm:text-lg leading-relaxed text-forge-100/90">{report.coaching_feedback}</p>
        </section>
      )}

      {report.next_steps?.next_question && (
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Next Step</div>
          <p className="font-serif text-2xl leading-snug text-forge-100">{report.next_steps.next_question}</p>
        </section>
      )}

      {report.reflection_summary?.length ? (
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Reflection Summary</div>
          <div className="space-y-4">
            {report.reflection_summary.map((item, index) => (
              <div key={`${item.label}-${index}`} className="border-l-2 border-white/10 pl-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-1">{item.label}</div>
                <p className="font-reading text-sm leading-relaxed text-forge-100/80">{item.response}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {report.debate_transcript?.length ? (
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Debate Transcript</div>
          <div className="space-y-5">
            {report.debate_transcript.map((turn, index) => (
              <div key={`${turn.label}-${index}`} className="border-l-2 border-white/10 pl-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-1">{turn.label}</div>
                <p className="font-reading text-sm leading-relaxed text-forge-100/80 whitespace-pre-wrap">{turn.text}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ListBlock({ title, items, tone }) {
  const color = tone === "gold" ? "text-gold-500" : "text-ember-500";
  return (
    <div className="bg-forge-900 p-6">
      <div className={`text-[10px] tracking-[0.3em] uppercase mb-3 ${color}`}>{title}</div>
      {items?.length ? (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={`${item}-${index}`} className="flex gap-2 text-sm text-forge-100/85">
              <span className={`${color} mt-1`}>◆</span>
              <span className="font-reading">{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-forge-100/40">No details recorded.</p>
      )}
    </div>
  );
}
